import React, { useState, useEffect } from 'react';
import '../styles/PatientList.css';
// Ensure you have your logo in src/assets/logo.png
import logo from '../images/logo.png'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faPen,
  faTrashAlt,
  faMagnifyingGlass,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleLeft,
  faAngleDoubleRight
} from '@fortawesome/free-solid-svg-icons';

function PatientList({ onPatientSelect, isSyncing }) {
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [patientsPerPage] = useState(10); 

  useEffect(() => {
    document.body.style.overflow = 'auto';
  }, []);

  // --- FETCH DATA ---
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/patients`)
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(data => {
        const transformedPatients = data.patients.map((patient, index) => ({
          id: patient.id || `patient-${index + 1}`,
          name: patient.name,
          dob: patient.dob || "Unknown",
          lastEncounterDate: patient.last_encounter_date || "Unknown",
        }));
        setPatients(transformedPatients);
      })
      .catch(error => {
        console.error("Error fetching patient list:", error);
        setError('Could not fetch patient list.');
        setPatients([]);
      });
  }, []);

  const handleActionClick = (actionType, patient, event) => {
    event.stopPropagation();
    if (actionType === 'view') {
      if(onPatientSelect) onPatientSelect(patient.name);
      return;
    }
    console.log(`${actionType} clicked for ${patient.name}`);
  };

  const handleSortChange = () => {
    const sorted = [...patients].sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.name.localeCompare(b.name);
      } else {
        return b.name.localeCompare(a.name);
      }
    });
    setPatients(sorted);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastPatient = currentPage * patientsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - patientsPerPage;
  const currentPatients = filteredPatients.slice(indexOfFirstPatient, indexOfLastPatient);
  const totalPages = Math.ceil(filteredPatients.length / patientsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
    }
  };

  return (
    <div className="patient-list-page">
      {/* --- TOP NAVIGATION BAR --- */}
      <nav className="top-navbar">
        <div className="logo-container">
          <img src={logo} alt="MyMR Logo" className="logo-img" />
        </div>
      </nav>

      {/* --- MAIN CONTENT --- */}
      <div className="patient-list-wrapper">
        <div className="patient-list-container">
          
          <div className="patient-list-header-row">
            <h2 className="patient-list-title">Patient list</h2>
            
            <div className="header-controls">
              <div className="search-wrapper">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search patients"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <button className="sort-btn" onClick={handleSortChange}>
                Sort ({sortOrder === 'asc' ? 'A-Z' : 'Z-A'})
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="patient-table">
              <thead>
                <tr>
                  <th className="th-sn">S.N</th>
                  <th className="th-name">Name</th>
                  <th className="th-dob">Date of Birth</th>
                  <th className="th-last">Last Encounter Date</th>
                  <th className="th-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {error ? (
                   <tr><td colSpan="5" className="error-msg">{error}</td></tr>
                ) : currentPatients.length > 0 ? (
                  currentPatients.map((patient, index) => {
                     const serialNumber = indexOfFirstPatient + index + 1;
                     return (
                      <tr key={patient.id}>
                          <td className="td-sn">{serialNumber}.</td>
                          <td className="td-name">{patient.name}</td>
                          <td className="td-dob">{patient.dob}</td>
                          <td className="td-last">{patient.lastEncounterDate}</td>
                          <td className="td-action">
                          <div className="action-icons">
                              <button
                              className="icon-btn view-btn tooltip-container"
                              onClick={(e) => handleActionClick('view', patient, e)}
                              >
                              <FontAwesomeIcon icon={faEye} />
                              <span className="tooltip">View</span>
                              </button>
                              <button
                              className="icon-btn edit-btn"
                              onClick={(e) => handleActionClick('edit', patient, e)}
                              >
                              <FontAwesomeIcon icon={faPen} />
                              </button>
                              <button
                              className="icon-btn delete-btn"
                              onClick={(e) => handleActionClick('delete', patient, e)}
                              >
                              <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                          </div>
                          </td>
                      </tr>
                     );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">No patients found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 0 && (
              <div className="pagination-footer">
                  <div className="page-info">
                      Page {currentPage} of {totalPages}
                  </div>
                  <div className="pagination-controls">
                      <button 
                          className="p-btn" 
                          onClick={() => goToPage(1)} 
                          disabled={currentPage === 1}
                      >
                          <FontAwesomeIcon icon={faAngleDoubleLeft} />
                      </button>
                      <button 
                          className="p-btn" 
                          onClick={() => goToPage(currentPage - 1)} 
                          disabled={currentPage === 1}
                      >
                          <FontAwesomeIcon icon={faAngleLeft} />
                      </button>
                      <button 
                          className="p-btn" 
                          onClick={() => goToPage(currentPage + 1)} 
                          disabled={currentPage === totalPages}
                      >
                          <FontAwesomeIcon icon={faAngleRight} />
                      </button>
                      <button 
                          className="p-btn" 
                          onClick={() => goToPage(totalPages)} 
                          disabled={currentPage === totalPages}
                      >
                          <FontAwesomeIcon icon={faAngleDoubleRight} />
                      </button>
                  </div>
              </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PatientList;